import React, { useRef, useState } from 'react';
import { ButtonColor } from '../../../../components/uikit/buttons/Button.tsx';
import clsx from 'clsx';
import { CustomModal } from '../../../../components/uikit/modal/CustomModal.tsx';
import { useScopedTranslation } from '../../../../application/localization/useScopedTranslation.ts';
import { useAdvancedForm } from '../../../../helpers/form/useAdvancedForm.ts';
import { useWatch } from 'react-hook-form';
import styles from './UserReportModal.module.scss';
import { useIsAuthorized } from '../../../../helpers/auth/auth-interceptor.ts';
import * as Sentry from '@sentry/react';
import { createId } from '../../../../components/uikit/type-utils.ts';
import { UserEnumRolesDto } from '../../../../services/api/api-client.types.ts';
import { TextArea } from '../../../../components/uikit/inputs/TextArea.tsx';
import { UseQueryResult } from '@tanstack/react-query';
import FeedbackError from './FeedbackError.ts';
import { QueryFactory } from '../../../../services/api';
import { RadioButton } from '../../../../components/uikit/RadioButton.tsx';
import ReportIcon from 'assets/icons/report-issue.svg?react';
import CancelIcon from 'assets/icons/close-button-small.svg?react';
import { ActionText } from '../../../../components/uikit/ActionText.tsx';
import { IconButton } from '../../../../components/uikit/IconButton.tsx';
import { useAnalytics } from 'helpers/analytics.ts';
import { UserMenuCategory } from '../RootPage.tsx';

export type Form = {
  name: string;
  email: string;
  comments: string;
  attachments: FileList | undefined;
  oneTime: boolean;
  recurring: boolean;
};

type OwnProps = {
  onClose: () => void;
  onSubmit: () => void;
  userQuery?: UseQueryResult<UserEnumRolesDto, unknown>;
};

const UserReportModal = (props: OwnProps) => {
  const i18n = useScopedTranslation('UserReportModal');
  const isAuth = useIsAuthorized() === true;
  const { trackEvent } = useAnalytics();

  const { onClose, onSubmit, userQuery } = props;

  const [fileDescription, setFileDescription] = useState<string | null>(null);

  const userQueryData = userQuery
    ? userQuery
    : QueryFactory.UserQuery.useGetCurrentUserInfoQuery({
        enabled: isAuth,
        staleTime: 5 * 60 * 1000,
        throwOnError: false,
      });

  const userName = userQueryData.data?.firstName;
  const userEmail = userQueryData.data?.email;

  const form = useAdvancedForm<Form>(
    async () => {
      trackEvent({
        category: UserMenuCategory,
        action: 'Send report',
        name: `Report sent`,
      });
      void reportToSentry();
    },
    {
      defaultValues: {
        name: '',
        email: '',
        comments: '',
        oneTime: false,
        recurring: false,
      },
    },
  );

  const [comments, isOneTime, isRecurring] = useWatch({
    control: form.control,
    name: ['comments', 'oneTime', 'recurring'],
  });

  const reportToSentry = async () => {
    if (!isOneTime && !isRecurring) {
      form.setError('oneTime', {
        message: i18n.t('required_error'),
      });

      return;
    }

    if (!comments) {
      form.setError('comments', {
        message: i18n.t('required_error'),
      });

      return;
    }

    const error = new FeedbackError('Report from user', comments);

    error.stack = undefined;

    const file = fileInputRef.current?.files?.[0] ?? null;

    const buffer = await file?.arrayBuffer();
    const uint8 = buffer ? new Uint8Array(buffer) : null;

    Sentry.captureException(error, (context) => {
      context.setLevel('debug'); // To mark such Sentry entries with a different color
      context.setTag('is-report-for-support', true);

      if (isAuth) {
        const trimmedEmail = userEmail?.trim();
        if (trimmedEmail) {
          context.setUser({
            userName: userName,
            email: trimmedEmail,
          });

          context.setExtra('user-contacts', trimmedEmail);
        }
      }
      context.setExtra('user-report-message', comments);
      context.setExtra(
        'user-issue-recurrence',
        isOneTime ? 'One-time' : 'Recurring',
      );

      if (uint8) {
        context.addAttachment({
          filename: file!.name,
          data: uint8,
          contentType: file!.type || 'application/octet-stream',
          attachmentType: 'event.attachment',
        });
      }

      context.setFingerprint([createId()]);
      return context;
    });
    onSubmit();
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const chooseOneTimeIssue = () => {
    form.clearErrors('oneTime');
    form.setValue('oneTime', true, { shouldDirty: true });
    form.setValue('recurring', false, { shouldDirty: true });
  };

  const chooseRecurringIssue = () => {
    form.clearErrors('oneTime');
    form.setValue('recurring', true, { shouldDirty: true });
    form.setValue('oneTime', false, { shouldDirty: true });
  };

  const triggerFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    form.clearErrors('attachments');
    const file = ev.target.files?.[0];
    if (!file) {
      return;
    }

    const MB20 = 20 * 1024 * 1024; // 20971520
    const MB1 = 1024 * 1024; // 1048576

    if (file.size > MB1) {
      form.setError('attachments', {
        message: i18n.t('max_size_file'),
      });

      resetInputFile();

      return;
    }

    setFileDescription(file.name);
  };

  const resetInputFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <CustomModal
      isOpen={true}
      onSubmit={form.handleSubmitDefault}
      onCancel={onClose}
      cancelButtonText={i18n.t('cancel_button')}
      submitButtonText={i18n.t('send_report_button')}
      submitButtonType={ButtonColor.Primary}
      cancelButtonType={ButtonColor.Secondary}
      titleText={i18n.t('title')}
      containerClassName={styles.height600}
    >
      <div className={clsx(styles.columnContainer, styles.rowGap32)}>
        <div>{i18n.t('text')}</div>
        <div className={clsx(styles.columnContainer, styles.rowGap8)}>
          <div className={clsx(styles.p1RegLine, styles.greyText)}>
            {i18n.t('problem_recurring_label')}
          </div>
          <div className={styles.relative}>
            <div className={clsx(styles.rowContainer, styles.columnGap162)}>
              <RadioButton
                name={'issue-recurrence'}
                title={i18n.t('one_time_radio')}
                value={'One-time'}
                checked={isOneTime}
                onChange={chooseOneTimeIssue}
                inputClassname={styles.marginLeft2}
              />
              <RadioButton
                name={'issue-recurrence'}
                title={i18n.t('recurring_radio')}
                value={'Recurring'}
                checked={isRecurring}
                onChange={chooseRecurringIssue}
                inputClassname={styles.marginLeft2}
              />
            </div>
            {form.formState.errors.oneTime && (
              <div className={clsx(styles.errorText, styles.absoluteError)}>
                {form.formState.errors.oneTime?.message}
              </div>
            )}
          </div>
        </div>
        <div className={styles.relative}>
          <TextArea
            {...form.register('comments')}
            onChange={(e) => {
              form.clearErrors('comments');
              form.setValue('comments', e.target.value, {
                shouldDirty: true,
              });
            }}
            topHelperText={i18n.t('description')}
            fullWidth={true}
            className={clsx(
              styles.descriptionContainer,
              form.formState.errors.comments && styles.errorField,
            )}
            containerClassName={styles.marginTop25}
          />
          {form.formState.errors.comments && (
            <div className={clsx(styles.errorText, styles.absoluteError)}>
              {form.formState.errors.comments?.message}
            </div>
          )}
        </div>
        <div
          className={clsx(
            styles.columnContainer,
            styles.rowGap12,
            styles.relative,
          )}
        >
          <div className={clsx(styles.rowContainer, styles.spaceBetween)}>
            <div className={clsx(styles.p1RegLine, styles.greyText)}>
              {i18n.t('attach_text')}
            </div>
            <div
              className={clsx(
                styles.p1RegLine,
                styles.greyText,
                styles.optional,
              )}
            >
              {i18n.t('optional')}
            </div>
          </div>
          {fileDescription ? (
            <div className={clsx(styles.rowContainer, styles.columnGap4)}>
              <div>{fileDescription}</div>
              <IconButton
                onClick={() => {
                  resetInputFile();
                  setFileDescription(null);
                }}
                type={'destroy'}
                iconClassName={styles.marginBottom1}
              >
                <CancelIcon />
              </IconButton>
            </div>
          ) : (
            <ActionText
              text={i18n.t('select_file')}
              onClickHandler={triggerFileUpload}
              Svg={ReportIcon}
              className={styles.columnGap8}
            />
          )}
          <input
            type="file"
            id="attachments"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          {form.formState.errors.attachments && (
            <div
              className={clsx(
                styles.errorText,
                styles.absoluteError,
                styles.bottom32,
              )}
            >
              {form.formState.errors.attachments.message}
            </div>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default UserReportModal;
